import { z } from 'zod';

import { entityIdSchema, nonEmptyTextSchema, sha256Schema, utcDateTimeSchema } from './primitives';
import { resourceScopeSchema } from './tenant-rbac';

const tokenSchema = z.string().trim().regex(/^[a-z0-9][a-z0-9-]{0,63}$/u);

/** A trusted pointer to content owned and locked by the calling business feature. */
export const approvalIntakeSourceSchema = z
  .object({
    sourceDomain: tokenSchema,
    documentKind: tokenSchema,
    documentId: entityIdSchema,
    documentVersionId: entityIdSchema,
    documentVersion: z.number().int().positive(),
    contentHash: sha256Schema,
    integrityReceiptId: entityIdSchema,
    scope: resourceScopeSchema,
  })
  .strict();

export const approvalIntakeTriggerSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('user-action'),
      action: tokenSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('workflow-event'),
      eventId: entityIdSchema,
      eventType: tokenSchema,
      occurredAt: utcDateTimeSchema,
    })
    .strict(),
]);

const presentationFieldSchema = z
  .object({ label: nonEmptyTextSchema.max(100), value: nonEmptyTextSchema.max(2_000) })
  .strict();

const presentationSectionSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('fields'),
      sectionId: entityIdSchema,
      title: nonEmptyTextSchema.max(120),
      fields: z.array(presentationFieldSchema).min(1).max(50),
    })
    .strict(),
  z
    .object({
      kind: z.literal('narrative'),
      sectionId: entityIdSchema,
      title: nonEmptyTextSchema.max(120),
      text: nonEmptyTextSchema.max(10_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal('table'),
      sectionId: entityIdSchema,
      title: nonEmptyTextSchema.max(120),
      columns: z
        .array(
          z
            .object({ key: tokenSchema, label: nonEmptyTextSchema.max(100) })
            .strict(),
        )
        .min(1)
        .max(20),
      rows: z.array(z.record(tokenSchema, z.string().max(2_000))).max(500),
    })
    .strict(),
  z
    .object({
      kind: z.literal('evidence'),
      sectionId: entityIdSchema,
      title: nonEmptyTextSchema.max(120),
      evidenceIds: z.array(entityIdSchema).min(1).max(100),
    })
    .strict(),
]);

export const approvalIntakePresentationSchema = z
  .object({
    title: nonEmptyTextSchema.max(200),
    subtitle: z.string().trim().max(300).nullable(),
    sections: z.array(presentationSectionSchema).min(1).max(30),
    sourceRouteId: entityIdSchema,
  })
  .strict();

export const approvalFreeLineMemberSchema = z
  .object({
    identityId: entityIdSchema,
    organizationGroupId: entityIdSchema,
  })
  .strict();

export const approvalCompletionBindingSchema = z
  .object({
    /** Server-registered handler ID. A browser-provided URL is never a callback authority. */
    handlerId: entityIdSchema,
    approvedEventType: tokenSchema,
    returnedEventType: tokenSchema,
  })
  .strict();

const intakeBase = {
  schemaVersion: z.literal(1),
  intakeId: entityIdSchema,
  idempotencyKey: entityIdSchema,
  trigger: approvalIntakeTriggerSchema,
  source: approvalIntakeSourceSchema,
  presentation: approvalIntakePresentationSchema,
  completion: approvalCompletionBindingSchema,
};

/**
 * The only entry contract business features need to implement.
 *
 * A workflow event may create a draft that waits for a free line, or submit immediately when the
 * source feature already collected that line. Named/fixed approvers are intentionally absent.
 */
export const approvalIntakeSchema = z.discriminatedUnion('submission', [
  z
    .object({
      ...intakeBase,
      submission: z.literal('review-before-submit'),
      lineSelection: z.null(),
    })
    .strict(),
  z
    .object({
      ...intakeBase,
      submission: z.literal('submit-with-line'),
      lineSelection: z.array(approvalFreeLineMemberSchema).min(1).max(20),
    })
    .strict(),
]);

/** Authoritative acknowledgement returned by the central intake boundary. */
export const approvalIntakeReceiptSchema = z
  .object({
    receiptId: entityIdSchema,
    intakeId: entityIdSchema,
    requestId: entityIdSchema.nullable(),
    status: z.enum(['awaiting-line', 'requested']),
    sourceDocumentVersionId: entityIdSchema,
    recordedAt: utcDateTimeSchema,
    serverSequence: z.number().int().positive(),
    immutable: z.literal(true),
  })
  .strict()
  .superRefine((receipt, context) => {
    if ((receipt.status === 'requested') !== (receipt.requestId !== null)) {
      context.addIssue({
        code: 'custom',
        path: ['requestId'],
        message: 'Only a submitted intake carries an approval request ID',
      });
    }
  });

export type ApprovalIntake = z.infer<typeof approvalIntakeSchema>;
export type ApprovalIntakePresentation = z.infer<typeof approvalIntakePresentationSchema>;
export type ApprovalIntakeReceipt = z.infer<typeof approvalIntakeReceiptSchema>;
